import { useState } from "react";
import { motion } from "framer-motion";
import MoviePicker from "./upload/MoviePicker";
import MovieWorkspace from "./upload/MovieWorkspace";

/**
 * The scenepack upload flow:
 *   1. pick the movie / TV show card (or create a new one),
 *   2. inside it: drop the scenepacks, and the poster / character designs, then publish.
 *
 * `presetCard`: already-known movie card (e.g. the one the creator is currently
 * browsing on their dashboard) — skips step 1 entirely and opens straight into
 * it, so adding another version of a character already on that card takes one
 * click instead of picking the same movie again from scratch.
 */
export default function ScenepackUploader({ onDone, presetMovieTitle = "", presetCard = null, lockCharacter = "", onPendingChange }) {
  const [card, setCard] = useState(presetCard); // the chosen movie card
  const [carried, setCarried] = useState([]); // files dropped on the first screen

  const step = card ? 2 : 1;

  return (
    <div data-testid="scenepack-uploader">
      {/* progress */}
      <div className="flex items-center gap-3 mb-8" data-testid="uploader-steps">
        {[["Movie card", 1], ["Scenepacks & designs", 2]].map(([label, n], i) => (
          <div key={n} className="flex items-center gap-3">
            {i > 0 && <div className={`w-10 h-px transition-colors ${step >= n ? "bg-[#880015]" : "bg-white/15"}`} />}
            <div className="flex items-center gap-2">
              <span className={`w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center transition ${step >= n ? "bg-[#880015] text-white shadow-[0_0_14px_rgba(136,0,21,0.6)]" : "bg-white/8 text-white/40"}`}>{n}</span>
              <span className={`text-[11px] font-mono uppercase tracking-wider ${step === n ? "text-white" : "text-white/40"}`}>{label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* The screen switches at once; the new one just fades and slides in. */}
      {!card ? (
        <motion.div key="pick" initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.22 }}>
          <MoviePicker presetTitle={presetMovieTitle} onPick={(c, files) => { setCard(c); setCarried(files || []); }} />
        </motion.div>
      ) : (
        <motion.div key="work" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.22 }}>
          <MovieWorkspace card={card} initialFiles={carried} lockCharacter={card.celebrity ? card.title : lockCharacter} onBack={() => { setCard(null); setCarried([]); }} onDone={onDone} onPendingChange={onPendingChange} />
        </motion.div>
      )}
    </div>
  );
}
